import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AuthUserInfo, ChangePasswordRequest } from '../../types/api';
import { apiPost } from '../../config/api';

const TOKEN_KEY = 'smartpm_token';
const USER_KEY = 'smartpm_user';
const TOKEN_EXPIRES_AT_KEY = 'smartpm_token_expires_at';

/** Token süresi: 2 saat (ms) */
export const TOKEN_LIFETIME_MS = 2 * 60 * 60 * 1000;

function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

function getStoredUser(): AuthUserInfo | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUserInfo) : null;
  } catch {
    return null;
  }
}

function getStoredTokenExpiresAt(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export interface AuthState {
  token: string | null;
  user: AuthUserInfo | null;
  /** Token'ın geçerlilik bitiş zamanı (timestamp, ms) */
  tokenExpiresAt: number | null;
}

const initialState: AuthState = {
  token: getStoredToken(),
  user: getStoredUser(),
  tokenExpiresAt: getStoredTokenExpiresAt(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ token: string; user: AuthUserInfo }>
    ) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.tokenExpiresAt = Date.now() + TOKEN_LIFETIME_MS;
      if (typeof window !== 'undefined') {
        localStorage.setItem(TOKEN_KEY, action.payload.token);
        localStorage.setItem(USER_KEY, JSON.stringify(action.payload.user));
        localStorage.setItem(TOKEN_EXPIRES_AT_KEY, String(state.tokenExpiresAt));
      }
    },
    clearCredentials: (state) => {
      state.token = null;
      state.user = null;
      state.tokenExpiresAt = null;
      if (typeof window !== 'undefined') {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
      }
    },
  },
});

/** POST /api/Auth/change-password — change current user's password. Returns 204 on success. */
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (body: ChangePasswordRequest, { rejectWithValue }) => {
    try {
      await apiPost<void>('/api/Auth/change-password', body);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şifre değiştirilemedi.');
    }
  }
);

export const { setCredentials, clearCredentials } = authSlice.actions;
export default authSlice.reducer;
